import React, { useEffect, useMemo, useState } from 'react';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import {
    PROFILE_IMAGE_UPDATED_EVENT,
    SEC_ApiCall,
    SEC_DataApiPost,
    getProfileImageUrlWithSchoolFallback,
    getSchoolLogoUrl
} from '../func.js';
import { userAppData } from '../MainApp.js';
import { AsyncActionButton, FileDropzone } from '../common/formComponents.js';
import useS3FileUpload from '../hooks/useS3FileUpload.js';
import LoadingComp from '../common/loading.js';
import './Profile.css';

const toastConfig = { autoClose: 4000, position: 'top-right' };

function parseLambdaPayload(response) {
    if (!response) {
        return {};
    }
    if (typeof response.body === 'string') {
        try {
            return JSON.parse(response.body);
        } catch (error) {
            return {};
        }
    }
    return response.body || response;
}

function syncGlobalProfileImage(profileImageId, profileImageUrl) {
    const nextProfileImageId = String(profileImageId || '').trim();
    const nextProfileImageUrl = getProfileImageUrlWithSchoolFallback(
        profileImageUrl,
        userAppData?.schoolCode
    );

    userAppData.profileImageId = nextProfileImageId;
    userAppData.profileImageUrl = nextProfileImageUrl;

    window.dispatchEvent(new CustomEvent(PROFILE_IMAGE_UPDATED_EVENT, {
        detail: {
            profileImageId: nextProfileImageId,
            profileImageUrl: nextProfileImageUrl,
        },
    }));
}

function defaultMfaData() {
    return {
        totp: {
            enabled: false,
            preferred: false,
            enabledAt: '',
            lastVerifiedAt: '',
        },
        devices: [],
    };
}

function formatUkDateTime(value) {
    if (!value) {
        return 'Not available';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(parsed);
}

function detectPlatformHint() {
    const userAgent = navigator.userAgent || '';
    if (/iPhone/i.test(userAgent)) return 'iPhone';
    if (/iPad/i.test(userAgent)) return 'iPad';
    if (/Android/i.test(userAgent)) return 'Android';
    if (/Windows/i.test(userAgent)) return 'Windows';
    if (/Macintosh/i.test(userAgent)) return 'Mac';
    return 'Unknown';
}

function createClientGuid() {
    if (window?.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
        const randomValue = Math.random() * 16 | 0;
        const mappedValue = character === 'x' ? randomValue : (randomValue & 0x3 | 0x8);
        return mappedValue.toString(16);
    });
}

function getImageExtension(file) {
    const mimeType = String(file?.type || '').toLowerCase();
    if (mimeType.includes('png')) return 'png';
    if (mimeType.includes('webp')) return 'webp';
    return 'jpg';
}

function loadImageFromFile(file) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        const objectUrl = URL.createObjectURL(file);
        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Failed to load selected image.'));
        };
        image.src = objectUrl;
    });
}

async function compressImageFile(file) {
    const sourceImage = await loadImageFromFile(file);
    const maxDimension = 900;
    const widthScale = maxDimension / sourceImage.width;
    const heightScale = maxDimension / sourceImage.height;
    const scale = Math.min(1, widthScale, heightScale);
    const targetWidth = Math.max(1, Math.round(sourceImage.width * scale));
    const targetHeight = Math.max(1, Math.round(sourceImage.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Image compression is not available in this browser.');
    }
    context.drawImage(sourceImage, 0, 0, targetWidth, targetHeight);

    const extension = getImageExtension(file);
    const outputMimeType = extension === 'png' ? 'image/png' : extension === 'webp' ? 'image/webp' : 'image/jpeg';
    const quality = outputMimeType === 'image/png' ? undefined : 0.82;

    const compressedBlob = await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Failed to compress image.'));
                return;
            }
            resolve(blob);
        }, outputMimeType, quality);
    });

    return new File([compressedBlob], `profile-image.${extension}`, { type: outputMimeType });
}

export default function Profile() {
    const [mfaData, setMfaData] = useState(defaultMfaData());
    const [profileImageId, setProfileImageId] = useState('');
    const [profileImageUrl, setProfileImageUrl] = useState(
        getProfileImageUrlWithSchoolFallback(
            userAppData?.profileImageUrl,
            userAppData?.schoolCode
        )
    );
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [profileImageFiles, setProfileImageFiles] = useState([]);
    const [showImageDropzone, setShowImageDropzone] = useState(false);
    const [isImageUpdating, setIsImageUpdating] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSetupOpen, setIsSetupOpen] = useState(false);
    const [setupStep, setSetupStep] = useState(1);
    const [setupPayload, setSetupPayload] = useState(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [deviceName, setDeviceName] = useState('My authenticator');
    const [platformHint, setPlatformHint] = useState(detectPlatformHint());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [renameDeviceId, setRenameDeviceId] = useState('');
    const [removeDeviceId, setRemoveDeviceId] = useState('');
    const [setupErrorMessage, setSetupErrorMessage] = useState('');
    const [confirmDeviceReplacement, setConfirmDeviceReplacement] = useState(false);
    const { uploadFiles } = useS3FileUpload('profileImage');

    const activeDevices = useMemo(() => {
        const devices = Array.isArray(mfaData?.devices) ? mfaData.devices : [];
        return devices.filter(device => String(device.status).toUpperCase() === 'ACTIVE');
    }, [mfaData]);

    const mfaEnabled = Boolean(mfaData?.totp?.enabled);
    const fallbackProfileImageUrl = getSchoolLogoUrl(userAppData?.schoolCode);
    const displayedProfileImage = profileImageUrl || fallbackProfileImageUrl;
    const hasStoredProfileImage = Boolean(profileImageId);

    useEffect(() => {
        loadProfileData();
    }, []);

    async function getFreshAccessToken() {
        const currentUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
        return currentUser?.signInUserSession?.accessToken?.jwtToken || '';
    }

    async function fetchProfileImageUrl(imageId) {
        const cleanImageId = String(imageId || '').trim();
        if (!cleanImageId) {
            setProfileImageUrl('');
            return '';
        }
        try {
            const signedImageUrl = await SEC_ApiCall('general/SignedURLs', {
                requestType: 'profileImage',
                locationId: userAppData?.cogId || '',
                fileId: cleanImageId,
                fileOldName: 'profile-image.jpg',
            });
            if (typeof signedImageUrl === 'string') {
                setProfileImageUrl(signedImageUrl);
                return signedImageUrl;
            } else {
                const payload = parseLambdaPayload(signedImageUrl);
                const resolvedImageUrl = typeof payload === 'string' ? payload : '';
                setProfileImageUrl(resolvedImageUrl);
                return resolvedImageUrl;
            }
        } catch (error) {
            console.error('Failed to load profile image URL', error);
            setProfileImageUrl('');
            return '';
        }
    }

    async function loadProfileData() {
        setIsLoading(true);
        try {
            const response = await SEC_ApiCall('profile');
            const payload = parseLambdaPayload(response);
            setMfaData(payload.mfaData || defaultMfaData());
            const imageIdFromApi = String(payload.profileImageId || '').trim();
            const imageUrlFromApi = String(payload.profileImageUrl || '').trim();
            const currentGlobalProfileImageUrl = String(userAppData?.profileImageUrl || '').trim();
            const hasExistingCustomProfileImage = Boolean(
                currentGlobalProfileImageUrl && currentGlobalProfileImageUrl !== fallbackProfileImageUrl
            );
            const apiReturnedFallbackImage = !imageUrlFromApi || imageUrlFromApi === fallbackProfileImageUrl;
            setProfileImageId(imageIdFromApi);

            // Preserve the already loaded sidebar image when the profile endpoint
            // falls back unexpectedly, then attempt a direct refresh from SignedURLs.
            if (imageIdFromApi && hasExistingCustomProfileImage && apiReturnedFallbackImage) {
                setProfileImageUrl(currentGlobalProfileImageUrl);
                syncGlobalProfileImage(imageIdFromApi, currentGlobalProfileImageUrl);

                const fetchedProfileImageUrl = await fetchProfileImageUrl(imageIdFromApi);
                if (fetchedProfileImageUrl) {
                    syncGlobalProfileImage(imageIdFromApi, fetchedProfileImageUrl);
                }
            } else if (imageUrlFromApi) {
                setProfileImageUrl(imageUrlFromApi);
                syncGlobalProfileImage(imageIdFromApi, imageUrlFromApi);
            } else {
                const fetchedProfileImageUrl = await fetchProfileImageUrl(imageIdFromApi);
                syncGlobalProfileImage(imageIdFromApi, fetchedProfileImageUrl);
            }
        } catch (error) {
            console.error('Failed to load profile data', error);
            toast.error('❌ Failed to load profile data.', toastConfig);
            setMfaData(defaultMfaData());
            setProfileImageId('');
            setProfileImageUrl('');
        } finally {
            setIsLoading(false);
        }
    }

    async function saveProfileImage() {
        if (profileImageFiles.length !== 1) {
            toast.error('❌ Please add one image file.', toastConfig);
            return;
        }

        setIsImageUpdating(true);
        try {
            const selectedFile = profileImageFiles[0]?.file;
            if (!selectedFile || !String(selectedFile.type || '').startsWith('image/')) {
                toast.error('❌ Please select a valid image file.', toastConfig);
                return;
            }

            // Compress before upload to reduce storage and login-time bandwidth.
            const compressedFile = await compressImageFile(selectedFile);
            const uploadGroupId = createClientGuid();
            const uploadFileDescriptor = {
                fileName: compressedFile.name,
                file: compressedFile,
                fileSize: compressedFile.size,
            };

            const uploadedFiles = await uploadFiles(
                [uploadFileDescriptor],
                userAppData?.cogId || '',
                { uploadGroupId }
            );

            const uploadedProfileImageId = uploadedFiles?.[0]?.fileId;
            if (!uploadedProfileImageId) {
                throw new Error('Missing uploaded profile image ID.');
            }

            toast.info('Your profile photo is being security scanned.', toastConfig);
            const updatePayload = await waitForProfileImagePromotion(uploadedProfileImageId);

            const returnedProfileImageId = String(
                updatePayload?.profileImageId || uploadedProfileImageId
            ).trim();
            const returnedProfileImageUrl = String(updatePayload?.profileImageUrl || '').trim();

            setProfileImageId(returnedProfileImageId);

            if (returnedProfileImageUrl) {
                setProfileImageUrl(returnedProfileImageUrl);
                syncGlobalProfileImage(returnedProfileImageId, returnedProfileImageUrl);
            } else {
                const fetchedProfileImageUrl = await fetchProfileImageUrl(returnedProfileImageId);
                syncGlobalProfileImage(returnedProfileImageId, fetchedProfileImageUrl);
            }

            setProfileImageFiles([]);
            setShowImageDropzone(false);
            toast.success('✅ Profile photo updated.', toastConfig);
        } catch (error) {
            console.error('Failed to update profile image', error);
            toast.error('❌ Failed to update profile photo.', toastConfig);
        } finally {
            setIsImageUpdating(false);
        }
    }

    async function waitForProfileImagePromotion(profileImageGuid) {
        const maxAttempts = 30;
        const pollIntervalMs = 2000;

        // GuardDuty scanning is asynchronous. Keep showing the current image
        // until the profile endpoint confirms the clean replacement is active.
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
            const profileResponse = await SEC_ApiCall('profile');
            const profilePayload = parseLambdaPayload(profileResponse);
            if (String(profilePayload?.profileImageId || '').trim() === profileImageGuid) {
                return profilePayload;
            }
            await new Promise(resolve => window.setTimeout(resolve, pollIntervalMs));
        }

        throw new Error('Profile image scan did not complete in time.');
    }

    async function changePassword() {
        if (!currentPassword || !newPassword || !confirmPassword) {
            toast.error('❌ Please complete all password fields.', toastConfig);
            return;
        }

        if (newPassword !== confirmPassword) {
            toast.error('❌ New password and confirmation do not match.', toastConfig);
            return;
        }

        setIsPasswordSubmitting(true);
        try {
            const response = await SEC_DataApiPost(
                'profile/resetPassword',
                JSON.stringify({
                    oldPassword: currentPassword,
                    newPassword,
                }),
            );
            const payload = parseLambdaPayload(response);

            if (payload?.updated) {
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                toast.success('✅ Password updated successfully.', toastConfig);
                return;
            }

            const lambdaMessage = String(
                payload?.error?.message ||
                payload?.message ||
                'Password change failed.'
            ).trim();

            console.error('Password change failed', payload);
            toast.error(`❌ ${lambdaMessage}`, toastConfig);
        } catch (error) {
            console.error('Password change request failed', error);
            toast.error(`❌ ${error?.message || 'Password change failed.'}`, toastConfig);
        } finally {
            setIsPasswordSubmitting(false);
        }
    }

    async function startMfaSetup() {
        setIsSubmitting(true);
        setSetupErrorMessage('');
        try {
            const accessToken = await getFreshAccessToken();
            const response = await SEC_DataApiPost(
                'profile/mfa/totp/setup',
                JSON.stringify({ accessToken }),
            );
            const payload = parseLambdaPayload(response);
            if (!payload?.secretCode || !payload?.otpauthUri) {
                setSetupPayload(null);
                setSetupErrorMessage('Sorry something went wrong. Please contact your system administrator for support');
                setIsSetupOpen(true);
                return;
            }
            setSetupPayload(payload);
            setSetupStep(1);
            setPlatformHint(detectPlatformHint());
            setDeviceName('My authenticator');
            setVerificationCode('');
            setConfirmDeviceReplacement(false);
            setIsSetupOpen(true);
        } catch (error) {
            console.error('Failed to start MFA setup', error);
            setSetupPayload(null);
            setSetupErrorMessage('Sorry something went wrong. Please contact your system administrator for support');
            setConfirmDeviceReplacement(false);
            setIsSetupOpen(true);
            toast.error('❌ Failed to start MFA setup.', toastConfig);
        } finally {
            setIsSubmitting(false);
        }
    }

    async function verifyTotpCode() {
        if (!/^\d{6}$/.test(verificationCode.trim())) {
            toast.error('❌ Enter a valid 6 digit code.', toastConfig);
            return;
        }
        setIsSubmitting(true);
        try {
            const accessToken = await getFreshAccessToken();
            const response = await SEC_DataApiPost(
                'profile/mfa/totp/verify',
                JSON.stringify({
                    code: verificationCode.trim(),
                    deviceName: deviceName.trim(),
                    platformHint: platformHint.trim(),
                    accessToken,
                }),
            );
            const payload = parseLambdaPayload(response);
            if (!payload?.verified) {
                toast.error('❌ Verification failed. Check the code and try again.', toastConfig);
                return;
            }
            setMfaData(payload.mfaData || defaultMfaData());
            closeSetupPanel();
            toast.success('✅ MFA enabled successfully.', toastConfig);
        } catch (error) {
            console.error('Failed to verify TOTP code', error);
            toast.error('❌ Failed to verify code.', toastConfig);
        } finally {
            setIsSubmitting(false);
        }
    }

    function closeSetupPanel() {
        setIsSetupOpen(false);
        setSetupPayload(null);
        setSetupErrorMessage('');
        setSetupStep(1);
        setVerificationCode('');
        setConfirmDeviceReplacement(false);
    }

    async function saveRename(deviceId) {
        if (!renameValue.trim()) {
            toast.error('❌ Device name is required.', toastConfig);
            return;
        }
        setIsSubmitting(true);
        try {
            const response = await SEC_DataApiPost(
                'profile/mfa/devices/rename',
                JSON.stringify({ deviceId, newDeviceName: renameValue.trim() }),
            );
            const payload = parseLambdaPayload(response);
            if (payload?.updated) {
                setMfaData(payload.mfaData || defaultMfaData());
                toast.success('✅ Device renamed.', toastConfig);
                setRenameDeviceId('');
                setRenameValue('');
            } else {
                toast.error('❌ Device could not be renamed.', toastConfig);
            }
        } catch (error) {
            console.error('Failed to rename device', error);
            toast.error('❌ Failed to rename device.', toastConfig);
        } finally {
            setIsSubmitting(false);
        }
    }

    async function removeDevice() {
        if (!removeDeviceId) {
            return;
        }
        setIsSubmitting(true);
        try {
            const accessToken = await getFreshAccessToken();
            const response = await SEC_DataApiPost(
                'profile/mfa/devices/remove',
                JSON.stringify({ deviceId: removeDeviceId, accessToken }),
            );
            const payload = parseLambdaPayload(response);
            if (payload?.updated) {
                setMfaData(payload.mfaData || defaultMfaData());
                toast.success('✅ Device removed.', toastConfig);
            } else {
                toast.error('❌ Device could not be removed.', toastConfig);
            }
        } catch (error) {
            console.error('Failed to remove device', error);
            toast.error('❌ Failed to remove device.', toastConfig);
        } finally {
            setRemoveDeviceId('');
            setIsSubmitting(false);
        }
    }

    async function copySecretKey() {
        if (!setupPayload?.secretCode) {
            return;
        }
        try {
            await navigator.clipboard.writeText(setupPayload.secretCode);
            toast.success('✅ Secret key copied.', toastConfig);
        } catch (error) {
            toast.error('❌ Could not copy secret key.', toastConfig);
        }
    }

    const qrCodeUrl = setupPayload?.otpauthUri
        ? `https://quickchart.io/qr?size=220&text=${encodeURIComponent(setupPayload.otpauthUri)}`
        : '';

    if (isLoading) {
        return <LoadingComp />;
    }

    return (
        <div className="profile-page">
            <div className="profile-card">
                <h1 className="profile-title">Profile</h1>
                <p className="profile-subtitle">{userAppData?.displayName || 'User'}</p>
                <p className="profile-subtitle">{userAppData?.email || ''}</p>
            </div>

            <div className="profile-card">
                <div className="profile-photo-header">
                    <h2 className="profile-card-title">Profile Photo</h2>
                </div>

                <div className="profile-photo-layout">
                    <div className="profile-photo-preview-column">
                        {displayedProfileImage ? (
                            <img
                                src={displayedProfileImage}
                                alt="Profile"
                                className="profile-photo-preview-image"
                            />
                        ) : (
                            <div className={hasStoredProfileImage ? 'profile-photo-preview-placeholder profile-photo-preview-placeholder-loading' : 'profile-photo-preview-placeholder'} />
                        )}
                        <button
                            type="button"
                            className="profile-action-button profile-photo-change-button"
                            onClick={() => setShowImageDropzone((previousState) => !previousState)}
                            disabled={isImageUpdating}
                        >
                            {showImageDropzone ? 'Hide uploader' : 'Change image'}
                        </button>
                    </div>

                    {showImageDropzone && (
                        <div className="profile-photo-dropzone-column">
                            <p className="profile-photo-warning">
                                Don&apos;t forget all other users in your school will potentially be able to see this photo depending on their use of the system and their level of permissions. See the Documentation section for more information.
                            </p>
                            <FileDropzone
                                filesState={profileImageFiles}
                                fileStateSet={setProfileImageFiles}
                                maxFiles={1}
                                maxTotalSizeMB={8}
                                labelText="Image Upload"
                                noteText="Only one profile image can be uploaded."
                                emptyText="No profile image selected."
                                accept="image/*"
                                multiple={false}
                                fullWidth
                            />
                            <div className="profile-photo-actions">
                                <AsyncActionButton
                                    type="button"
                                    className="profile-action-button"
                                    onClick={saveProfileImage}
                                    disabled={profileImageFiles.length !== 1}
                                    isLoading={isImageUpdating}
                                    loadingText="Updating image..."
                                >
                                    Save image
                                </AsyncActionButton>
                                <button
                                    type="button"
                                    className="profile-small-button profile-small-button-muted"
                                    onClick={() => {
                                        setShowImageDropzone(false);
                                        setProfileImageFiles([]);
                                    }}
                                    disabled={isImageUpdating}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="profile-card">
                <div className="profile-password-header">
                    <div>
                        <h2 className="profile-card-title">Change Password</h2>
                        <p className="profile-info-row">Update your School Engine password for future sign-ins.</p>
                        <p className="profile-info-row">Your password must be a minimum of 10 characters and include at least 1 special character.</p>
                    </div>
                </div>

                <div className="profile-password-form">
                    <input
                        type="password"
                        className="profile-input"
                        placeholder="Current password"
                        value={currentPassword}
                        onChange={(event) => setCurrentPassword(event.target.value)}
                    />
                    <input
                        type="password"
                        className="profile-input"
                        placeholder="New password"
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                    />
                    <input
                        type="password"
                        className="profile-input"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                    />
                </div>

                <div className="profile-password-actions">
                    <AsyncActionButton
                        type="button"
                        className="profile-action-button"
                        onClick={changePassword}
                        isLoading={isPasswordSubmitting}
                        loadingText="Updating password..."
                    >
                        Update password
                    </AsyncActionButton>
                </div>
            </div>

            <div className="profile-card">
                <div className="profile-mfa-header">
                    <div>
                        <h2 className="profile-card-title">Multi factor authentication</h2>
                        <span className={mfaEnabled ? 'profile-badge profile-badge-on' : 'profile-badge profile-badge-off'}>
                            {mfaEnabled ? 'On' : 'Off'}
                        </span>
                    </div>
                    <AsyncActionButton
                        type="button"
                        className="profile-action-button"
                        onClick={startMfaSetup}
                        isLoading={isSubmitting}
                        loadingText="Setting up..."
                    >
                        Set up MFA
                    </AsyncActionButton>
                </div>

                <div>
                    <h3 className="profile-section-heading">Manage devices</h3>
                    {activeDevices.length === 0 && (
                        <p className="profile-info-row">No active remembered devices.</p>
                    )}
                    {activeDevices.length > 0 && (
                        <div className="profile-device-list">
                            {activeDevices.map((device) => (
                                <div className="profile-device-card" key={device.deviceId}>
                                    <div className="profile-device-main">
                                        <p className="profile-device-name">{device.deviceName || 'Authenticator device'}</p>
                                        <p className="profile-device-meta">
                                            {device.platformHint || 'Unknown'} | Added {formatUkDateTime(device.addedAt)} | Last used {formatUkDateTime(device.lastUsedAt)}
                                        </p>
                                    </div>
                                    <div className="profile-device-actions">
                                        {renameDeviceId === device.deviceId ? (
                                            <div className="profile-rename-row">
                                                <input
                                                    type="text"
                                                    className="profile-input"
                                                    value={renameValue}
                                                    onChange={(event) => setRenameValue(event.target.value)}
                                                />
                                                <AsyncActionButton
                                                    type="button"
                                                    className="profile-small-button"
                                                    onClick={() => saveRename(device.deviceId)}
                                                    isLoading={isSubmitting}
                                                    loadingText="Saving..."
                                                >
                                                    Save
                                                </AsyncActionButton>
                                                <button
                                                    type="button"
                                                    className="profile-small-button profile-small-button-muted"
                                                    onClick={() => {
                                                        setRenameDeviceId('');
                                                        setRenameValue('');
                                                    }}
                                                    disabled={isSubmitting}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="profile-inline-buttons">
                                                <button
                                                    type="button"
                                                    className="profile-small-button"
                                                    onClick={() => {
                                                        setRenameDeviceId(device.deviceId);
                                                        setRenameValue(device.deviceName || '');
                                                    }}
                                                >
                                                    Rename
                                                </button>
                                                <button
                                                    type="button"
                                                    className="profile-small-button profile-small-button-danger"
                                                    onClick={() => setRemoveDeviceId(device.deviceId)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {isSetupOpen && (
                <div className="profile-modal-overlay">
                    <div className="profile-modal">
                        <h2 className="profile-card-title">Set up MFA</h2>

                        {setupErrorMessage && (
                            <div>
                                <p className="profile-info-row">{setupErrorMessage}</p>
                            </div>
                        )}

                        {!setupErrorMessage && setupStep === 1 && (
                            <div>
                                <p className="profile-info-row">Step 1: Install Microsoft Authenticator or Google Authenticator on your phone.</p>
                                <label className="profile-checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={confirmDeviceReplacement}
                                        onChange={(event) => setConfirmDeviceReplacement(event.target.checked)}
                                    />
                                    <span>I understand that adding a new device will make any current MFA devices inactive</span>
                                </label>
                                <AsyncActionButton
                                    type="button"
                                    className="profile-action-button"
                                    onClick={() => setSetupStep(2)}
                                    disabled={!confirmDeviceReplacement}
                                >
                                    Continue
                                </AsyncActionButton>
                            </div>
                        )}

                        {!setupErrorMessage && setupStep === 2 && (
                            <div>
                                <p className="profile-info-row">Step 2: Scan the QR code below, or enter the secret key manually.</p>
                                {qrCodeUrl && <img src={qrCodeUrl} alt="MFA QR Code" className="profile-qr-image" />}
                                <p className="profile-secret">{setupPayload?.secretCode}</p>
                                <button type="button" className="profile-small-button" onClick={copySecretKey}>Copy secret key</button>
                                <div className="profile-modal-actions">
                                    <AsyncActionButton type="button" className="profile-action-button" onClick={() => setSetupStep(3)}>
                                        Continue
                                    </AsyncActionButton>
                                </div>
                            </div>
                        )}

                        {!setupErrorMessage && setupStep === 3 && (
                            <div>
                                <p className="profile-info-row">Step 3: Enter your 6 digit code, then verify.</p>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    className="profile-input"
                                    placeholder="6 digit code"
                                    value={verificationCode}
                                    onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, ''))}
                                />
                                <input
                                    type="text"
                                    className="profile-input"
                                    placeholder="Device name"
                                    value={deviceName}
                                    onChange={(event) => setDeviceName(event.target.value)}
                                />
                                <div className="profile-modal-actions">
                                    <AsyncActionButton
                                        type="button"
                                        className="profile-action-button"
                                        onClick={verifyTotpCode}
                                        isLoading={isSubmitting}
                                        loadingText="Verifying..."
                                    >
                                        Verify
                                    </AsyncActionButton>
                                </div>
                            </div>
                        )}
                        <button type="button" className="profile-modal-close" onClick={closeSetupPanel} disabled={isSubmitting}>Close</button>
                    </div>
                </div>
            )}

            {removeDeviceId && (
                <div className="profile-modal-overlay">
                    <div className="profile-modal profile-modal-small">
                        <h3 className="profile-card-title">Remove device</h3>
                        <p className="profile-info-row">Are you sure you want to remove this remembered device?</p>
                        <div className="profile-modal-actions">
                            <AsyncActionButton
                                type="button"
                                className="profile-small-button profile-small-button-danger"
                                onClick={removeDevice}
                                isLoading={isSubmitting}
                                loadingText="Removing..."
                            >
                                Remove
                            </AsyncActionButton>
                            <button type="button" className="profile-small-button profile-small-button-muted" onClick={() => setRemoveDeviceId('')} disabled={isSubmitting}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
